
import { 
  Users, 
  Clock, 
  TrendingUp, 
  Target, 
  Calendar, 
  Award, 
  BookOpen, 
  UserCheck,
  Heart,
  Shield,
  Briefcase,
  Star,
  Brain,
  Zap,
  BarChart3,
  GraduationCap,
  MapPin,
  MessageSquare,
  DollarSign,
  Home,
  Headphones,
  Globe,
  Activity,
  Smile,
  FileText,
  Settings
} from 'lucide-react';
import type { Product } from './types';

export const hrProducts: Product[] = [
  // Complete HR Management Platform is now a Live product - imported separately in index.ts
  {
    id: 'performance-management-platform',
    title: 'Performance Management Platform',
    subtitle: 'Employee performance optimisation',
    description: 'Advanced performance management with goal setting, continuous feedback, performance reviews, and development planning designed for UK employment standards.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#performance-management', '#employee-development', '#goal-setting', '#feedback', '#talent-management'],
    features: [
      'Goal Setting & OKR Management',
      'Continuous Feedback & Check-ins',
      'Performance Review Cycles',
      '360-Degree Feedback System',
      'Development Planning & Tracking',
      'Competency Management',
      'Performance Analytics & Insights',
      'Career Path Planning'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'employee-engagement-sentiment-platform',
    title: 'Employee Engagement & Sentiment Platform',
    subtitle: 'Workforce engagement & mood intelligence',
    description: 'Comprehensive employee engagement platform combining pulse surveys, sentiment tracking, recognition programmes, culture analytics, and real-time mood analysis for UK workplace standards.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#employee-engagement', '#culture', '#recognition', '#surveys', '#workplace-analytics', '#sentiment', '#mood-analysis', '#pulse-surveys'],
    features: [
      'Employee Pulse Surveys & Feedback',
      'Real-Time Sentiment Tracking',
      'AI-Powered Emotion Analysis',
      'Anonymous Feedback Channels',
      'Recognition & Rewards Programmes with UK Tax Compliance',
      'Culture Analytics & Insights',
      'Team Building & Collaboration Tools',
      'Wellness & Mental Health Support',
      'Communication & Announcement Tools',
      'Manager Alert & Notification System',
      'Trend Analysis & Predictions',
      'Team Sentiment Comparison',
      'Engagement Analytics & Reporting',
      'Action Planning & Follow-up',
      'Integration with Communication Tools',
      'Sentiment Dashboard & Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'employee-learning-development-platform',
    title: 'Employee Learning & Development Platform',
    subtitle: 'Personalized learning & skill development',
    description: 'Advanced L&D platform combining course management, AI-powered learning recommendations, personalized learning paths, skills tracking, certification programmes, and comprehensive learning analytics for UK professional development.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#learning-development', '#training', '#skills-management', '#certification', '#employee-growth', '#learning-experience', '#personalized-learning', '#career-growth'],
    features: [
      'Course Creation & Management',
      'AI-Powered Learning Recommendations',
      'Personalized Learning Paths',
      'Skills Assessment & Tracking',
      'Skills Gap Analysis & Closure',
      'Certification & Compliance Programmes with UK Professional Bodies',
      'Learning Path Planning',
      'Social Learning & Collaboration',
      'Microlearning & Bite-Sized Content',
      'Mobile Learning Applications',
      'Learning Analytics & Progress Tracking',
      'Integration with HR Systems',
      'Integration with Content Libraries',
      'Certification & Badge Management',
      'ROI Measurement & Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-teal-600'
  },
  {
    id: 'time-attendance-system',
    title: 'Time & Attendance System',
    subtitle: 'Advanced workforce management',
    description: 'Comprehensive time and attendance management with biometric integration, scheduling, overtime tracking, and UK employment law compliance.',
    icon: Clock,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#time-attendance', '#workforce-management', '#scheduling', '#payroll-integration', '#compliance'],
    features: [
      'Biometric Time Clock Integration',
      'Employee Scheduling & Shift Management',
      'Overtime & Break Tracking',
      'Leave Management & Approval',
      'Payroll Integration & Processing',
      'Compliance & Labor Law Monitoring',
      'Mobile Time Tracking Applications',
      'Real-time Reporting & Analytics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'skills-mapping-platform',
    title: 'Skills Mapping Platform',
    subtitle: 'Workforce capability intelligence',
    description: 'Identifies and tracks employee skills to match them with projects, mentors, and development opportunities.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#skills-management', '#talent-development', '#workforce-planning', '#competency-mapping', '#career-development'],
    features: [
      'Comprehensive Skills Assessment',
      'Skills Gap Analysis & Identification',
      'Project-Skill Matching Algorithm',
      'Career Path Planning & Recommendations',
      'Learning Resource Suggestions',
      'Mentorship Matching System',
      'Skills-Based Team Formation',
      'Competency Framework Management',
      'Progress Tracking & Analytics',
      'Integration with Learning Platforms'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'inclusive-hiring-platform',
    title: 'Inclusive Hiring Platform',
    subtitle: 'Bias-free recruitment solution',
    description: 'Uses AI to reduce bias in recruitment by anonymizing applications and using structured evaluation criteria.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#inclusive-hiring', '#diversity-recruiting', '#bias-reduction', '#ai-recruitment', '#fair-hiring'],
    features: [
      'AI-Powered Bias Detection & Removal',
      'Anonymous Resume Screening',
      'Structured Interview Frameworks',
      'Diverse Candidate Sourcing',
      'Inclusive Job Description Analysis',
      'Unconscious Bias Training Integration',
      'Diversity Analytics & Reporting',
      'Fair Assessment & Scoring',
      'Compliance & Audit Trails',
      'Integration with Existing ATS'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'employee-performance-analytics',
    title: 'Employee Performance Analytics',
    subtitle: 'Data-driven performance insights',
    description: 'Measures and analyses performance data to identify trends, predictors of success, and development needs with UK employment standards.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#performance-analytics', '#hr-analytics', '#employee-insights', '#predictive-hr', '#talent-analytics'],
    features: [
      'Performance Data Analysis & Trends',
      'Predictive Performance Modeling',
      'Goal Achievement Tracking',
      'Peer Performance Benchmarking',
      'Development Need Identification',
      'High Performer Pattern Analysis',
      'Retention Risk Prediction',
      'Custom Performance Metrics',
      'Manager Coaching Insights',
      'Real-Time Performance Dashboards'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'workforce-planning-tool',
    title: 'Workforce Planning Tool',
    subtitle: 'Strategic talent forecasting',
    description: 'Models different organisational scenarios and predicts future talent needs based on business strategy with UK employment forecasting.',
    icon: MapPin,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#workforce-planning', '#talent-forecasting', '#strategic-hr', '#organizational-planning', '#hr-strategy'],
    features: [
      'Strategic Workforce Modeling',
      'Talent Demand Forecasting',
      'Skills & Competency Planning',
      'Scenario Planning & What-If Analysis',
      'Succession Planning & Pipelines',
      'Budget & Resource Allocation',
      'Integration with Business Strategy',
      'Retirement & Turnover Prediction',
      'Hiring & Development Planning',
      'Workforce Analytics & Insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-600'
  },
  {
    id: 'benefits-optimization',
    title: 'Benefits Optimization Platform',
    subtitle: 'Personalized benefits guidance',
    description: 'Helps employees select and utilize benefits packages based on their individual circumstances and preferences.',
    icon: DollarSign,
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#benefits-optimization', '#employee-benefits', '#personalized-benefits', '#benefits-guidance', '#healthcare-benefits'],
    features: [
      'Personalized Benefits Recommendations',
      'Cost Comparison & Analysis',
      'Decision Support Tools',
      'Benefits Utilization Tracking',
      'Open Enrollment Automation',
      'Employee Benefits Education',
      'Healthcare Cost Predictions',
      'Flexible Benefits Management',
      'Integration with Payroll Systems',
      'Benefits Analytics & Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-emerald-500 to-green-600'
  },
  {
    id: 'remote-work-enablement',
    title: 'Remote Work Enablement',
    subtitle: 'Distributed workforce support',
    description: 'Provides tools and analytics to support distributed teams, including home office setup and virtual collaboration.',
    icon: Home,
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#remote-work', '#distributed-teams', '#virtual-collaboration', '#home-office', '#hybrid-work'],
    features: [
      'Remote Onboarding & Training',
      'Home Office Setup Guidance',
      'Virtual Collaboration Tools',
      'Remote Team Building Activities',
      'Digital Wellness Programs',
      'Performance Management for Remote',
      'Communication & Culture Tools',
      'Technology Support & IT Help',
      'Remote Work Policy Management',
      'Productivity & Engagement Analytics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'hr-service-delivery',
    title: 'HR Service Delivery Platform',
    subtitle: 'Automated HR support system',
    description: 'Automates HR inquiries and transactions through chatbots and self-service portals for enhanced employee experience.',
    icon: Headphones,
    image: 'https://images.unsplash.com/photo-1556155092-8707de31f9c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#hr-service-delivery', '#employee-self-service', '#hr-automation', '#chatbots', '#hr-support'],
    features: [
      'AI-Powered HR Chatbots',
      'Employee Self-Service Portal',
      'Automated Ticket Routing',
      'Knowledge Base & FAQ Management',
      'Case Management & Tracking',
      'Multi-Channel Support (Chat, Email, Phone)',
      'SLA Management & Reporting',
      'Integration with HRIS Systems',
      'Custom Workflow Automation',
      'Employee Satisfaction Surveys'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-600'
  },
  {
    id: 'diversity-inclusion-analytics',
    title: 'Diversity & Inclusion Analytics',
    subtitle: 'Workplace equity measurement',
    description: 'Measures and tracks diversity, equity, and inclusion metrics to promote fair and inclusive workplace practices.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#diversity-inclusion', '#workplace-equity', '#dei-analytics', '#inclusive-culture', '#bias-measurement'],
    features: [
      'Diversity Metrics & Dashboards',
      'Pay Equity Analysis & Reporting',
      'Inclusive Culture Surveys',
      'Bias Detection & Analysis',
      'D&I Goal Setting & Tracking',
      'Representation Analytics',
      'Inclusive Hiring Metrics',
      'Anonymous Feedback Channels',
      'Benchmark & Industry Comparison',
      'Action Planning & Initiatives Tracking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-600'
  },
  {
    id: 'employee-journey-mapping',
    title: 'Employee Journey Mapping',
    subtitle: 'Lifecycle experience optimisation',
    description: 'Maps and optimises the entire employee lifecycle from recruitment to retirement for enhanced experience with UK employment law compliance.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#employee-journey', '#employee-experience', '#lifecycle-management', '#touchpoint-optimization', '#hr-analytics'],
    features: [
      'Employee Lifecycle Mapping',
      'Touchpoint Experience Analysis',
      'Journey Stage Optimization',
      'Experience Measurement & Feedback',
      'Critical Moment Identification',
      'Persona-Based Journey Customization',
      'Experience Benchmark & Comparison',
      'Action Planning & Implementation',
      'Cross-Department Collaboration',
      'Journey Analytics & Insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-amber-500 to-orange-600'
  },
  {
    id: 'compensation-analysis-tool',
    title: 'Compensation Analysis Tool',
    subtitle: 'Strategic pay equity platform',
    description: 'Analyses compensation data to ensure fair pay practices and competitive market positioning with UK equal pay legislation compliance.',
    icon: DollarSign,
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#compensation-analysis', '#pay-equity', '#salary-benchmarking', '#compensation-planning', '#fair-pay'],
    features: [
      'Market Salary Benchmarking',
      'Pay Equity Analysis & Reporting',
      'Compensation Band Management',
      'Merit Increase Planning',
      'Bonus & Incentive Analysis',
      'Total Rewards Modeling',
      'Compliance & Audit Support',
      'Custom Compensation Surveys',
      'ROI & Budget Impact Analysis',
      'Executive Compensation Planning'
    ],
    pricing: { setup: 35840, monthly: 1649 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'exit-interview-management',
    title: 'Exit Interview Management',
    subtitle: 'Turnover insights & retention',
    description: 'Systematically captures and analyses exit interview data to identify retention opportunities and improve workplace culture with UK employment law guidance.',
    icon: Smile,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#exit-interviews', '#retention-analytics', '#turnover-reduction', '#employee-feedback', '#culture-improvement'],
    features: [
      'Structured Exit Interview Process',
      'Anonymous Feedback Collection',
      'Turnover Trend Analysis',
      'Root Cause Identification',
      'Manager & Department Insights',
      'Retention Strategy Development',
      'Benchmark & Industry Comparison',
      'Action Planning & Follow-up',
      'Predictive Turnover Modeling',
      'Executive Turnover Dashboards'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'hr-compliance-dashboard',
    title: 'HR Compliance Dashboard',
    subtitle: 'Regulatory compliance monitoring',
    description: 'Monitors and manages HR compliance requirements across multiple jurisdictions and regulatory frameworks.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Human Resources',
    status: 'Available',
    businessTypes: ['#hr-compliance', '#regulatory-monitoring', '#audit-management', '#policy-compliance', '#risk-management'],
    features: [
      'Multi-Jurisdiction Compliance Tracking',
      'Automated Compliance Alerts',
      'Policy Management & Distribution',
      'Audit Trail & Documentation',
      'Training Compliance Monitoring',
      'Risk Assessment & Mitigation',
      'Regulatory Change Management',
      'Compliance Reporting & Analytics',
      'Employee Certification Tracking',
      'Legal Update & Advisory Services'
    ],
    pricing: { setup: 40960, monthly: 1884 },
    color: 'from-red-500 to-orange-600'
  }
];
